import {
  MessageType,
  type ServerMessage,
  type ClientMessage,
  type InputState,
  type LobbyStateMessage,
  type MatchStartMessage,
  type StateSnapshotMessage,
  type MatchEndMessage,
  type RewardDistributedMessage,
  type LobbyPlayer,
  type World,
  type MatchResult,
  type PlayerId,
  type PayoutMode,
} from '@mine-or-die/shared';

/**
 * Settlement data - received after match is settled on-chain by server
 */
export interface SettlementData {
  matchId: string;
  txHash: string; // Settlement transaction hash
  winners: string[];
  amounts: string[];
}

export type ConnectionState = 'disconnected' | 'connecting' | 'connected';
export type GameState = 'lobby' | 'countdown' | 'playing' | 'results';

export interface NetClientEvents {
  onConnect: () => void;
  onDisconnect: () => void;
  onError: (error: string) => void;
  onLobbyUpdate: (
    players: LobbyPlayer[],
    myId: PlayerId,
    countdown: number | null,
    matchId?: string,
    escrowAddress?: string,
    payoutMode?: PayoutMode
  ) => void;
  onMatchStart: (world: World, matchId?: string, escrowAddress?: string) => void;
  onStateSnapshot: (tick: number, lastProcessedInput: number[], state: World) => void;
  onMatchEnd: (result: MatchResult) => void;
  onSettlement?: (settlement: SettlementData) => void;
}

/**
 * WebSocket client for game networking
 */
export class NetClient {
  private socket: WebSocket | null = null;
  private serverUrl: string;
  private events: NetClientEvents;

  private _connectionState: ConnectionState = 'disconnected';
  private _gameState: GameState = 'lobby';
  private _myPlayerId: PlayerId = -1;
  private _inputSequence: number = 0;

  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000;

  constructor(serverUrl: string, events: NetClientEvents) {
    this.serverUrl = serverUrl;
    this.events = events;
  }

  get connectionState(): ConnectionState {
    return this._connectionState;
  }

  get gameState(): GameState {
    return this._gameState;
  }

  get myPlayerId(): PlayerId {
    return this._myPlayerId;
  }

  get inputSequence(): number {
    return this._inputSequence;
  }

  /**
   * Connect to the server
   */
  connect(): void {
    if (this._connectionState !== 'disconnected') return;

    this._connectionState = 'connecting';

    try {
      this.socket = new WebSocket(this.serverUrl);

      this.socket.onopen = () => {
        console.log('Connected to server');
        this._connectionState = 'connected';
        this.reconnectAttempts = 0;
        this.events.onConnect();
      };

      this.socket.onclose = () => {
        console.log('Disconnected from server');
        this._connectionState = 'disconnected';
        this.events.onDisconnect();
        this.attemptReconnect();
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.events.onError('Connection error');
      };

      this.socket.onmessage = (event) => {
        this.handleMessage(event.data);
      };
    } catch (error) {
      console.error('Failed to connect:', error);
      this._connectionState = 'disconnected';
      this.events.onError('Failed to connect');
    }
  }

  /**
   * Disconnect from the server
   */
  disconnect(): void {
    this.maxReconnectAttempts = 0; // Prevent reconnect
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this._connectionState = 'disconnected';
  }

  /**
   * Attempt to reconnect
   */
  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    console.log(`Reconnecting (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);

    setTimeout(() => {
      this.connect();
    }, this.reconnectDelay * this.reconnectAttempts);
  }

  /**
   * Send a message to the server
   */
  private send(message: ClientMessage): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      return;
    }
    this.socket.send(JSON.stringify(message));
  }

  /**
   * Handle incoming server message
   */
  private handleMessage(data: string): void {
    let message: ServerMessage;
    try {
      message = JSON.parse(data);
    } catch {
      console.error('Failed to parse server message:', data);
      return;
    }

    console.log('NetClient received message type:', message.type, '(0x' + message.type.toString(16) + ')');

    switch (message.type) {
      case MessageType.LOBBY_STATE: {
        const lobby = message as LobbyStateMessage;
        this._myPlayerId = lobby.yourPlayerId;
        this._gameState = lobby.countdown !== null ? 'countdown' : 'lobby';
        this.events.onLobbyUpdate(
          lobby.players,
          lobby.yourPlayerId,
          lobby.countdown,
          lobby.matchId,
          lobby.escrowAddress,
          lobby.payoutMode
        );
        break;
      }

      case MessageType.PLAYER_JOINED:
      case MessageType.PLAYER_LEFT:
        // These are handled via LOBBY_STATE updates
        break;

      case MessageType.MATCH_START: {
        console.log('MATCH_START received! Calling onMatchStart callback...');
        const start = message as MatchStartMessage;
        this._gameState = 'playing';
        this._inputSequence = 0;
        this.events.onMatchStart(start.initialState, start.matchId, start.escrowAddress);
        console.log('onMatchStart callback completed');
        break;
      }

      case MessageType.STATE_SNAPSHOT: {
        const snapshot = message as StateSnapshotMessage;
        this.events.onStateSnapshot(
          snapshot.tick,
          snapshot.lastProcessedInput,
          snapshot.state
        );
        break;
      }

      case MessageType.MATCH_END: {
        const end = message as MatchEndMessage;
        this._gameState = 'results';
        this.events.onMatchEnd(end.result);
        break;
      }

      case MessageType.PONG:
        // Could calculate latency here
        break;

      case MessageType.ERROR:
        console.error('Server error:', message);
        this.events.onError((message as any).message);
        break;

      case MessageType.STAKE_CONFIRMED:
        // Handle stake confirmation - update lobby UI
        console.log('Stake confirmed:', message);
        break;

      case MessageType.REWARD_DISTRIBUTED: {
        // Handle reward distribution - settlement completed on-chain
        const reward = message as RewardDistributedMessage;
        console.log('Rewards distributed:', reward);
        if (this.events.onSettlement) {
          this.events.onSettlement({
            matchId: reward.matchId,
            txHash: reward.txHash,
            winners: reward.winners,
            amounts: reward.amounts,
          });
        }
        break;
      }

      default:
        console.log('Unknown server message type:', (message as any).type);
    }
  }

  /**
   * Join the lobby
   */
  joinLobby(playerName: string, walletAddress?: string): void {
    this.send({
      type: MessageType.JOIN_LOBBY,
      playerName,
      walletAddress,
    });
  }

  /**
   * Join the practice room (with NPCs)
   */
  joinPractice(playerName: string): void {
    this.send({
      type: MessageType.JOIN_PRACTICE,
      playerName,
    });
  }

  /**
   * Join a winner-takes-all room
   */
  joinWinnerTakesAll(playerName: string, walletAddress?: string): void {
    this.send({
      type: MessageType.JOIN_WINNER_TAKES_ALL,
      playerName,
      walletAddress,
    });
  }

  /**
   * Set ready state
   */
  setReady(ready: boolean): void {
    this.send({
      type: MessageType.READY,
      ready,
    });
  }

  /**
   * Send input to server
   */
  sendInput(input: InputState): number {
    this._inputSequence++;

    this.send({
      type: MessageType.INPUT,
      sequence: this._inputSequence,
      input,
    });

    return this._inputSequence;
  }

  /**
   * Send ping
   */
  ping(): void {
    this.send({
      type: MessageType.PING,
      timestamp: Date.now(),
    });
  }

  /**
   * Notify server of successful stake
   */
  sendStakeConfirmation(matchId: string, walletAddress: string, txHash: string): void {
    this.send({
      type: MessageType.STAKE_REQUEST,
      matchId,
      walletAddress,
      txHash,
    });
  }
}
